import React from 'react'
import { makeStyles } from '@material-ui/styles'
import { formatDateLocale } from '../../../../../lib/utils.es6'

const ARROW_H = 56
const NOTCH = 20

const useStyle = makeStyles(function() {
  return {
    card: {
      background: '#FFFFFF',
      borderRadius: '8px',
      marginTop: '30px',
      padding: '30px'
    },
    cardTitle: {
      fontFamily: 'Lato',
      fontStyle: 'normal',
      fontWeight: 600,
      fontSize: '16px',
      lineHeight: '24px',
      letterSpacing: '0.0015em',
      color: '#000000',
      marginBottom: '20px'
    },
    stagesRow: {
      display: 'flex',
      alignItems: 'stretch'
    },
    stageColumn: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      minWidth: 0
    },
    arrow: {
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      height: ARROW_H,
      paddingLeft: 20,
      paddingRight: NOTCH + 10,
      color: '#FFFFFF',
      fontFamily: 'Lato',
      fontWeight: 600,
      fontSize: '13px',
      overflow: 'visible',
      // White outline triangle — 2px larger than the coloured tip — acts as a
      // separator so adjacent same-colour arrows are visually distinct.
      '&::before': {
        content: '""',
        position: 'absolute',
        right: -(NOTCH + 2),
        top: -2,
        width: 0,
        height: 0,
        borderTop: `${ARROW_H / 2 + 2}px solid transparent`,
        borderBottom: `${ARROW_H / 2 + 2}px solid transparent`,
        borderLeft: `${NOTCH + 2}px solid white`,
        zIndex: 1,
        pointerEvents: 'none'
      },
      '&::after': {
        content: '""',
        position: 'absolute',
        right: -NOTCH,
        top: 0,
        width: 0,
        height: 0,
        borderTop: `${ARROW_H / 2}px solid transparent`,
        borderBottom: `${ARROW_H / 2}px solid transparent`,
        zIndex: 2,
        pointerEvents: 'none'
      }
    },
    arrowFirst: {
      borderRadius: '6px 0 0 6px'
    },
    arrowNotFirst: {
      paddingLeft: NOTCH + 20
    },
    arrowLast: {
      borderRadius: '0 6px 6px 0',
      '&::before': { display: 'none' },
      '&::after': { display: 'none' }
    },
    arrowApproved: {
      background: '#388E3C',
      '&::after': { borderLeft: `${NOTCH}px solid #388E3C` }
    },
    arrowRejected: {
      background: '#C62828',
      '&::after': { borderLeft: `${NOTCH}px solid #C62828` }
    },
    arrowActive: {
      background: '#1565C0',
      '&::after': { borderLeft: `${NOTCH}px solid #1565C0` }
    },
    arrowNeedAction: {
      background: '#E65100',
      '&::after': { borderLeft: `${NOTCH}px solid #E65100` }
    },
    arrowReturned: {
      background: '#6D4C41',
      '&::after': { borderLeft: `${NOTCH}px solid #6D4C41` }
    },
    arrowWaiting: {
      background: '#BDBDBD',
      '&::after': { borderLeft: `${NOTCH}px solid #BDBDBD` }
    },
    // Circular icon badge inside the arrow.
    arrowIcon: {
      width: 30,
      height: 30,
      borderRadius: '50%',
      background: 'rgba(255, 255, 255, 0.25)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      fontWeight: 700,
      fontSize: '13px',
      lineHeight: 1
    },
    arrowLabel: {
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      textOverflow: 'ellipsis',
      flex: 1,
      minWidth: 0
    },
    meta: {
      padding: '12px 16px 0 20px',
      flex: 1
    },
    metaNotFirst: {
      paddingLeft: NOTCH + 20
    },
    metaDecision: {
      fontFamily: 'Open Sans',
      fontSize: '12px',
      color: '#616161',
      marginBottom: '2px'
    },
    metaReason: {
      fontFamily: 'Open Sans',
      fontSize: '12px',
      color: '#424242',
      marginTop: '6px',
      whiteSpace: 'pre-wrap',
      borderTop: '1px solid #EEEEEE',
      paddingTop: '6px'
    },
    metaNote: {
      fontFamily: 'Open Sans',
      fontSize: '12px',
      color: '#9E9E9E',
      fontStyle: 'italic'
    }
  }
})

const ApprovalStages = function(props) {
  const classes = useStyle()
  const policy = props.policy

  if (
    !policy ||
    !Array.isArray(policy.maker_checks) ||
    !policy.maker_checks.length
  ) {
    return null
  }

  const checks = policy.maker_checks.slice().sort(function(a, b) {
    return (a.sequence || 0) - (b.sequence || 0)
  })

  let anyRealRejected = false
  let maxSeq = 0
  for (let k = 0; k < checks.length; k++) {
    if (checks[k].status === 'Rejected') anyRealRejected = true
    if ((checks[k].sequence || 0) > maxSeq) maxSeq = checks[k].sequence || 0
  }

  const pStatus = policy.status || ''
  let premiumStatus
  if (
    policy.active ||
    pStatus === 'active' ||
    pStatus === 'Full_Coverage' ||
    pStatus === 'In_Arrears'
  ) {
    premiumStatus = 'Approved'
  } else if (anyRealRejected) {
    // Policy blocked at a maker-check — premium collection not yet reachable.
    premiumStatus = 'Blocked'
  } else {
    // Includes 'Unpaid' (first Pending here → blue active) and 'Un_Confirmed'
    // (real Pending checks come first → this one stays gray).
    premiumStatus = 'Pending'
  }

  checks.push({
    guid: 'premium-collection-approval',
    label: 'Premium Collection',
    type: 'PremiumCollectionApproval',
    sequence: maxSeq + 1,
    status: premiumStatus
  })

  // The first Pending check (in sequence) is the active one awaiting review.
  let activeCheck = null
  for (let i = 0; i < checks.length; i++) {
    if (checks[i].status === 'Pending') {
      activeCheck = checks[i]
      break
    }
  }

  const needAction =
    policy.additional_details && policy.additional_details.need_action
  const amlNeedAction =
    policy.additional_details && policy.additional_details.aml_need_action

  function arrowColorClass(check) {
    if (check.status === 'Approved') return classes.arrowApproved
    if (check.status === 'Rejected') return classes.arrowRejected
    if (check.status === 'Returned') return classes.arrowReturned
    if (activeCheck && check.guid === activeCheck.guid) {
      return needAction || amlNeedAction
        ? classes.arrowNeedAction
        : classes.arrowActive
    }
    return classes.arrowWaiting
  }

  function iconFor(check, seq) {
    if (check.status === 'Approved') return '✓'
    if (check.status === 'Rejected') return '✗'
    if (check.status === 'Returned') return '↩'
    if (activeCheck && check.guid === activeCheck.guid)
      return needAction || amlNeedAction ? '!' : '●'
    return seq
  }

  return (
    <section className="mb-3" style={{ marginBottom: '30px' }}>
      <div className={classes.stagesRow}>
        {checks.map(function(check, idx) {
          const isFirst = idx === 0
          const isLast = idx === checks.length - 1
          const seq = check.sequence || idx + 1
          const isActive = activeCheck && check.guid === activeCheck.guid
          const isWaiting = check.status === 'Pending' && !isActive
          const isRejected = check.status === 'Rejected'
          const isApproved = check.status === 'Approved'
          const isReturned = check.status === 'Returned'

          const arrowClass = [
            classes.arrow,
            arrowColorClass(check),
            isFirst ? classes.arrowFirst : classes.arrowNotFirst,
            isLast ? classes.arrowLast : ''
          ].join(' ')

          const metaClass = [
            classes.meta,
            isFirst ? '' : classes.metaNotFirst
          ].join(' ')

          return (
            <div
              key={check.guid}
              className={classes.stageColumn}
              style={{ zIndex: checks.length - idx }}
            >
              <div className={arrowClass}>
                <span className={classes.arrowIcon}>{iconFor(check, seq)}</span>
                <span className={classes.arrowLabel}>
                  {check.label || check.type}
                </span>
              </div>

              <div className={metaClass}>
                {isApproved && check.approver_full_name && check.decided_at ? (
                  <div className={classes.metaDecision}>
                    Approved by {check.approver_full_name}
                    <br />
                    {formatDateLocale(check.decided_at)}
                  </div>
                ) : isApproved && check.decided_at ? (
                  <div className={classes.metaDecision}>
                    Approved {formatDateLocale(check.decided_at)}
                  </div>
                ) : isApproved ? (
                  <div className={classes.metaDecision}>Approved</div>
                ) : isReturned &&
                  check.approver_full_name &&
                  check.decided_at ? (
                  <div className={classes.metaDecision} style={{ color: '#6D4C41' }}>
                    Returned by {check.approver_full_name}
                    <br />
                    {formatDateLocale(check.decided_at)}
                  </div>
                ) : isReturned && check.decided_at ? (
                  <div className={classes.metaDecision} style={{ color: '#6D4C41' }}>
                    Returned {formatDateLocale(check.decided_at)}
                  </div>
                ) : isReturned ? (
                  <div className={classes.metaDecision} style={{ color: '#6D4C41' }}>
                    Returned
                  </div>
                ) : isRejected &&
                  check.approver_full_name &&
                  check.decided_at ? (
                  <div className={classes.metaDecision}>
                    Rejected by {check.approver_full_name}
                    <br />
                    {formatDateLocale(check.decided_at)}
                  </div>
                ) : isRejected && check.decided_at ? (
                  <div className={classes.metaDecision}>
                    Rejected {formatDateLocale(check.decided_at)}
                  </div>
                ) : isActive && check.type === 'PremiumCollectionApproval' ? (
                  <div className={classes.metaNote}>
                    Awaiting first premium payment
                  </div>
                ) : isActive && amlNeedAction ? (
                  <div
                    className={classes.metaNote}
                    style={{ color: '#E65100' }}
                  >
                    AML action required — awaiting resolution
                  </div>
                ) : isActive && needAction ? (
                  <div
                    className={classes.metaNote}
                    style={{ color: '#E65100' }}
                  >
                    Changes requested — awaiting resubmission
                  </div>
                ) : isActive ? (
                  <div className={classes.metaNote}>Awaiting review</div>
                ) : isWaiting ? (
                  <div className={classes.metaNote}>
                    Waiting for prior stage
                  </div>
                ) : null}

                {check.reason ? (
                  <div className={classes.metaReason}>{check.reason}</div>
                ) : null}
              </div>
            </div>
          )
        })}
      </div>
    </section>
  )
}

export default ApprovalStages
